import React, { Fragment, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { times } from 'lodash';
import { connect } from 'react-redux';
import Button from 'reactstrap/lib/Button';

import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { HomeModulesModel } from 'client/data/models/home-modules';
import { DEFAULT_CONTENT, transform } from 'client/data/cms/content';
import { CmsEntities } from 'client/data/models/cms';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { AdLabel } from 'site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { StickyIcon } from 'site-modules/shared/components/sticky-icon/sticky-icon';
import { AdUnit } from 'site-modules/shared/components/ad-unit/ad-unit';

import './car-news-marquee.scss';

const HERO_HPM_ADS = 4;
const HERO_HPM_ADS_PROPS = {
  // desktop
  false: { adName: 'hpmarquee', md: true, lg: true, xl: true },
  // mobile
  true: { adName: 'mhpmarquee', xs: true, sm: true },
};

const URL_TARGET = { true: '_blank', false: '_self' };
const TRACKING_ID = { true: 'hero_module_link_click_out', false: 'hero_module_link_click' };

const SCROLL_BTN_CLASS_NAME = 'scroll-btn p-0 d-flex justify-content-center align-items-center';

function Card({ item, isMobile, position }) {
  const aspectRatio = '257:164';
  const suffix = isMobile ? 'Mobile' : '';
  const urlTargetNew = item.metadata('urlTargetNew').boolean();

  return (
    <a
      href={item.metadata(`url${suffix}`).value()}
      target={URL_TARGET[urlTargetNew]}
      data-tracking-id={TRACKING_ID[urlTargetNew]}
      data-tracking-value={item.metadata('verbiageNoMarkup').value()}
      data-tracking-position={position}
      rel="noopener noreferrer"
      className="m-card bg-white d-flex flex-column justify-content-between text-decoration-none overflow-hidden h-100 pos-r rounded-12"
      style={{ width: '257px' }}
    >
      <div>
        <AspectRatioContainer aspectRatio={aspectRatio}>
          <img
            alt=""
            src={getStaticImageUrl(item.metadata(`imageMobile`).value())}
            className="w-100 h-100 pos-a center-y"
            loading="lazy"
            decoding="async"
          />
        </AspectRatioContainer>
        <ContentFragment classes="fw-bold px-1_25 pt-1_25 mb-2">{item.metadata('verbiage').value()}</ContentFragment>
      </div>
      <div className="px-1_25 mb-1_25">
        <div className="btn btn-outline-blue-50 medium fw-medium py-0_5 mt-auto text-wrap">
          {item.metadata('ctaText').value('Learn more')}
          <StickyIcon
            className={classnames('small', {
              'icon-open_in_new': urlTargetNew,
              'icon-arrow-right3': !urlTargetNew,
            })}
          />
        </div>
        <div className="small text-blue-50">{item.metadata('oemSite').value()}</div>
      </div>
    </a>
  );
}

Card.propTypes = {
  item: CmsEntities.Content.isRequired,
  isMobile: PropTypes.bool,
  position: PropTypes.number,
};

Card.defaultProps = {
  isMobile: false,
  position: null,
};

export function CarNewsMarqueeUI({ content, className, isMobile, componentType, scrollBtnPosition }) {
  const listRef = useRef();

  const handleScrollBy = useCallback(direction => {
    const element = listRef.current;
    element.scrollBy({ left: direction * window.innerWidth, behavior: 'smooth' });
  }, []);

  function handleLeftBtnClick() {
    handleScrollBy(-1);
  }

  function handleRightBtnClick() {
    handleScrollBy(1);
  }

  const cards = content.children();

  return (
    <Fragment>
      {cards.length ? (
        <MinHeightWrapper componentType={componentType} className={classnames('car-news-marquee', className)}>
          <div
            className="rounded-8 overflow-hidden bg-blue-100 px-1 py-1_5"
            data-tracking-parent="edm-entry-home-page-hero"
          >
            <div
              id="marquee-list"
              className={classnames('pos-r text-gray-darkest mb-1', {
                'heading-4': !isMobile,
                'fw-bold': isMobile,
              })}
            >
              Consider These Recommendations
              <AdLabel classes="fw-normal" />
            </div>
            <ul
              ref={listRef}
              className="card-list list-unstyled mb-0 d-flex pos-r px-0_5 pb-0_5"
              aria-labelledby="marquee-list"
            >
              {cards.map((item, index) => (
                <li key={item.id}>
                  <Card item={item} isMobile={isMobile} position={index + 1} />
                </li>
              ))}
            </ul>
            {(cards.length > 2 || isMobile) && (
              <div className={classnames('d-flex pt-1', `justify-content-${scrollBtnPosition}`)}>
                <Button color="" className={`${SCROLL_BTN_CLASS_NAME} me-0_5`} onClick={handleLeftBtnClick}>
                  <span className="visually-hidden">left scroll</span>
                  <i className="size-16 text-cool-gray-30 icon-arrow-left3" aria-hidden />
                </Button>
                <Button color="" className={SCROLL_BTN_CLASS_NAME} onClick={handleRightBtnClick}>
                  <span className="visually-hidden">right scroll</span>
                  <i className="size-16 text-cool-gray-30 icon-arrow-right3" aria-hidden />
                </Button>
              </div>
            )}
          </div>
        </MinHeightWrapper>
      ) : (
        <div className="car-news-marquee empty-marquee" aria-hidden data-tracking-parent="edm-entry-home-page-hero" />
      )}
      <div>
        {times(HERO_HPM_ADS, i => (
          <AdUnit
            key={i}
            {...HERO_HPM_ADS_PROPS[isMobile]}
            position={`${i + 1}`}
            renderWhenViewable={false}
            oneByOne
            is1x1
          />
        ))}
      </div>
    </Fragment>
  );
}

CarNewsMarqueeUI.propTypes = {
  content: CmsEntities.Content,
  className: PropTypes.string,
  isMobile: PropTypes.bool,
  componentType: PropTypes.string,
  scrollBtnPosition: PropTypes.string,
};

CarNewsMarqueeUI.defaultProps = {
  content: DEFAULT_CONTENT,
  className: null,
  isMobile: false,
  componentType: 'NEWS_MARQUEE',
  scrollBtnPosition: 'end',
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const CarNewsMarquee = connect(mapStateToProps)(
  connectToModel(CarNewsMarqueeUI, {
    content: bindToPath('homeHeroContent', HomeModulesModel, transform),
  })
);
